// api/stickiesApi.js
import axios from 'axios';

const API_BASE_URL = '/api';

/**
 * Fetches all stickies for a given note
 * @param {string} noteId - The ID of the note
 * @returns {Promise<Array>} Array of sticky notes
 */
export const fetchStickies = async (noteId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/notes/${noteId}/stickies`,
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching stickies:', error);
    throw error;
  }
};

/**
 * Creates a new sticky note
 * @param {string} noteId - The ID of the note
 * @param {Object} stickyData - The sticky note data
 * @returns {Promise<Object>} Created sticky note
 */
export const createSticky = async (noteId, stickyData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/notes/${noteId}/stickies`,
      stickyData,
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating sticky:', error);
    throw error;
  }
};

/**
 * Updates an existing sticky note
 * @param {string} noteId - The ID of the note
 * @param {string} stickyId - The ID of the sticky note
 * @param {Object} updates - The updates to apply
 * @returns {Promise<Object>} Updated sticky note
 */
export const updateSticky = async (noteId, stickyId, updates, isMainPanelUpdate = false) => {
  try {
    // Separate update data based on panel type
    const updateData = isMainPanelUpdate ? {
      main_x: updates.main_x,
      main_y: updates.main_y,
      main_width: updates.main_width,
      main_height: updates.main_height,
      content: updates.content,
      title: updates.title,  // ADD THIS
      z_index: updates.zIndex,
      type: updates.type,
      list_order: updates.list_order,
      tag1: updates.tag1,  // Add to both
      tag2: updates.tag2,  // Add to both
    } : {
      x: updates.x,
      y: updates.y,
      width: updates.width,
      height: updates.height,
      main_x: updates.main_x,        // ADD THIS
      main_y: updates.main_y,        // ADD THIS  
      main_width: updates.main_width,  // ADD THIS
      main_height: updates.main_height, // ADD THIS
      content: updates.content,
      title: updates.title,  // ADD THIS
      type: updates.type,
      is_minimized: updates.is_minimized,
      z_index: updates.zIndex,
      display_mode: updates.display_mode,
      list_order: updates.list_order,
      tag1: updates.tag1,  // Add to both
      tag2: updates.tag2,  // Add to both

    };

    const response = await axios.put(
      `${API_BASE_URL}/notes/${noteId}/stickies/${stickyId}`,
      updateData,
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating sticky:', error);
    throw error;
  }
};

/**
 * Deletes a sticky note
 * @param {string} noteId - The ID of the note
 * @param {string} stickyId - The ID of the sticky to delete
 * @returns {Promise<void>}
 */
export const deleteSticky = async (noteId, stickyId) => {
  try {
    await axios.delete(
      `${API_BASE_URL}/notes/${noteId}/stickies/${stickyId}`,
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      }
    );
  } catch (error) {
    console.error('Error deleting sticky:', error);
    throw error;
  }
};

/**
 * Updates layout for multiple stickies in batch
 * @param {string} noteId - The ID of the note
 * @param {Array} layoutUpdates - Array of sticky layout updates
 * @returns {Promise<Object>} Updated layout data
 */
export const updateStickyLayouts = async (noteId, layoutUpdates) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/notes/${noteId}/stickies/batch`,
      layoutUpdates,
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating sticky layouts:', error);
    throw error;
  }
};

/**
 * Fetch tag domains from system config
 * @returns {Promise<Object>} Tag domains object
 */
export const fetchTagDomains = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/config/tag-domains`,
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching tag domains:', error);
    throw error;
  }
};

/**
* Fetches all checklist items for a specific sticky note
* @param {string} noteId - The ID of the note
* @param {string} stickyId - The ID of the sticky note
* @returns {Promise<Array>} Array of checklist items
*/
export const fetchChecklistItems = async (noteId, stickyId) => {
 try {
   const response = await axios.get(
     `${API_BASE_URL}/notes/${noteId}/stickies/${stickyId}/checklist`,
     {
       headers: { 'Content-Type': 'application/json' },
       withCredentials: true
     }
   );
   return response.data;
 } catch (error) {
   console.error('Error fetching checklist items:', error);
   throw error;
 }
};

/**
* Creates a new checklist item
* @param {string} noteId - The ID of the note
* @param {string} stickyId - The ID of the sticky note
* @param {Object} itemData - The checklist item data
* @returns {Promise<Object>} Created checklist item
*/
export const createChecklistItem = async (noteId, stickyId, itemData) => {
 try {
   const response = await axios.post(
     `${API_BASE_URL}/notes/${noteId}/stickies/${stickyId}/checklist`,
     itemData,
     {
       headers: { 'Content-Type': 'application/json' },
       withCredentials: true
     }
   );
   return response.data;
 } catch (error) {
   console.error('Error creating checklist item:', error);
   throw error;
 }
};

/**
* Updates an existing checklist item
* @param {string} noteId - The ID of the note
* @param {string} stickyId - The ID of the sticky note
* @param {string} itemId - The ID of the checklist item
* @param {Object} updates - The updates to apply
* @returns {Promise<Object>} Updated checklist item
*/
export const updateChecklistItem = async (noteId, stickyId, itemId, updates) => {
 try {
   const response = await axios.put(
     `${API_BASE_URL}/notes/${noteId}/stickies/${stickyId}/checklist/${itemId}`,
     updates,
     {
       headers: { 'Content-Type': 'application/json' },
       withCredentials: true
     }
   );
   return response.data;
 } catch (error) {
   console.error('Error updating checklist item:', error);
   throw error;
 }
};

/**
* Deletes a checklist item
* @param {string} noteId - The ID of the note
* @param {string} stickyId - The ID of the sticky note
* @param {string} itemId - The ID of the checklist item to delete
* @returns {Promise<void>}
*/
export const deleteChecklistItem = async (noteId, stickyId, itemId) => {
 try {
   await axios.delete(
     `${API_BASE_URL}/notes/${noteId}/stickies/${stickyId}/checklist/${itemId}`,
     {
       headers: { 'Content-Type': 'application/json' },
       withCredentials: true
     }
   );
 } catch (error) {
   console.error('Error deleting checklist item:', error);
   throw error;
 }
};

/**
 * Create multiple stickies in a single batch request
 * @param {number} noteId - The ID of the note to create stickies for
 * @param {Array} stickies - Array of sticky data objects
 * @returns {Promise<Array>} The created stickies
 */
export const createStickiesBatch = async (noteId, stickies) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/notes/${noteId}/stickies/batch`,
      { stickies },
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating stickies batch:', error);
    throw error;
  }
};

// Add this to your stickiesApi.js file
export const updateStickyOrders = async (noteId, orderUpdates) => {
    console.log('🔍 API DEBUG - updateSticky called with:');
  console.log('  noteId:', noteId);
  console.log('  stickyId:', stickyId);
  console.log('  stickyData:', stickyData);
  console.log('  stickyData JSON:', JSON.stringify(stickyData));
  const response = await fetch(`${API_BASE_URL}/notes/${noteId}/stickies/reorder`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ updates: orderUpdates }),
  });

  if (!response.ok) {
    throw new Error('Failed to update sticky order');
  }

  return response.json();
};